import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ZoomIn } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { GALLERY, GALLERY_CATEGORIES } from "@/content";

export default function Gallery() {
  const [cat, setCat] = useState("ALL");
  const filtered = cat === "ALL" ? GALLERY : GALLERY.filter((g) => g.category === cat);

  return (
    <section id="gallery" data-testid="gallery-section" className="relative py-20 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_30%,rgba(255,0,85,0.06),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="The Gallery"
          title={<>Moments In <span className="text-neon">Neon</span></>}
          sub="Packed floors, laser skies and behind-the-decks chaos. Tap any shot to open it full size in a new page."
        />

        <Reveal className="flex flex-wrap gap-2 mb-10">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 rounded-full border transition-colors ${
                cat === c
                  ? "bg-volt text-ink border-volt font-bold"
                  : "border-white/15 text-zinc-400 hover:border-volt/50 hover:text-volt"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="columns-2 md:columns-3 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map((g) => (
              <motion.a
                layout
                key={g.src}
                href={g.src}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="relative mb-4 block break-inside-avoid group cursor-pointer overflow-hidden rounded-xl"
                data-testid={`gallery-item-${g.category.toLowerCase().replace(/\s+/g, "-")}`}
                aria-label={`Open ${g.alt} full size in a new page`}
              >
                {g.type === "video" ? (
                  <video
                    className="w-full object-cover group-hover:scale-110 transition-transform duration-700"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    poster={g.poster}
                  >
                    <source src={g.src} type="video/mp4" />
                    {g.webm && <source src={g.webm} type="video/webm" />}
                  </video>
                ) : (
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="w-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                )}
                {g.type === "video" && (
                  <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase bg-volt text-ink px-2.5 py-1 rounded-full font-bold">
                    Video
                  </span>
                )}
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors flex items-center justify-center">
                  <ZoomIn className="w-8 h-8 text-volt opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-[0_0_12px_rgba(204,255,0,0.7)]" />
                </div>
                <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
                  {g.category}
                </span>
              </motion.a>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
